import React from 'react';

import { formatSeparateByThousands, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

/**
 * The workforce's shape by level, as a pyramid.
 *
 * "Level" is the pay rate — a relabel of an existing concept, not a new field.
 * Tiers arrive already ordered by rate midpoint, highest first, so the apex sits
 * at the top and the reader can see at a glance whether the organisation is
 * top-heavy.
 *
 * Plain divs rather than a chart: a pyramid is a stack of proportional bars, and
 * an ApexCharts instance here would inherit the Dashboard's hidden-tabpanel
 * resize problem for no gain.
 *
 * `midpoint` is absent for a user without pay-rate visibility. The distribution
 * still renders — the level's NAME is not pay information, and the recruitment
 * panel already shows it — only the band figure goes.
 */
export default function PeopleLevelStructure({ levelStructure }) {
    const tiers = levelStructure?.tiers ?? [];
    const unrated = levelStructure?.unrated_positions ?? 0;

    if (tiers.length === 0) {
        return (
            <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">
                {unrated > 0 ? $t('No position in scope carries a pay rate, so there is no level distribution to show.') : $t('No positions in scope.')}
            </p>
        );
    }

    const widest = Math.max(...tiers.map((tier) => tier.count));
    const rated = levelStructure.rated_positions ?? tiers.reduce((sum, tier) => sum + tier.count, 0);

    return (
        <div>
            <div className="tw-space-y-2">
                {tiers.map((tier) => (
                    <div key={tier.payrate_id} className="tw-flex tw-items-center tw-gap-x-3">
                        <div className="tw-w-48 tw-shrink-0 tw-min-w-0">
                            <div className="tw-text-sm tw-text-charcoal tw-truncate">{tier.label}</div>
                            {'midpoint' in tier && (
                                <div className="tw-text-xs tw-text-charcoal-50">
                                    {$t('midpoint {{amount}}', { amount: formatToFixedMoneyWithLocale(tier.midpoint) })}
                                </div>
                            )}
                        </div>
                        <div className="tw-flex-1 tw-flex tw-justify-center">
                            <div
                                className="tw-h-5 tw-bg-blue tw-rounded"
                                style={{ width: `${Math.max((tier.count / widest) * 100, 2)}%` }}
                                title={`${tier.label}: ${formatSeparateByThousands(tier.count)}`}
                            />
                        </div>
                        <div className="tw-w-24 tw-shrink-0 tw-text-end">
                            <span className="tw-text-sm tw-text-charcoal tw-font-semibold">{formatSeparateByThousands(tier.count)}</span>
                            <span className="tw-text-xs tw-text-charcoal-50 tw-ms-1">({Math.round((tier.count / rated) * 100)}%)</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Said out loud rather than left implicit: Secondee, Volunteer, Consultant
                and any salary-override-priced Paid position carry no pay rate, so a
                pyramid covering 4 of 40 positions would otherwise read as the whole
                workforce. */}
            {unrated > 0 && (
                <p className="tw-text-xs tw-text-charcoal-50 tw-mt-4 tw-mb-0">
                    {/* Deliberately NOT named `count`: i18next treats `count` as the
                        plural selector and expects a number, so passing a formatted
                        string there drives plural resolution off a non-number. */}
                    {$t(
                        '{{positions}} positions carry no pay rate and sit in no level — Secondee, Volunteer, Consultant and salary-override Paid roles are priced without one.',
                        { positions: formatSeparateByThousands(unrated) },
                    )}
                </p>
            )}
        </div>
    );
}
